// src/config/manuscriptThemes.js
//
// Manuscript themes: the look of one book — its typefaces, margins, chapter
// openings, ornaments. **Not `src/theme.js`**, which is the app's own design
// tokens. Nothing here is product chrome; every value is the author's, so the
// colours below are data and carry `design-tokens-allow`.
//
// The shape matches `backend/utils/manuscript_theme.py`, which checks every value
// a theme is saved or printed with. Keep the two in step: a key the backend does
// not know is dropped on save.
//
// The presets are the Theme Editor prototype's, less what was left out of a
// manuscript theme on purpose: the trim size stays on the manuscript, and the
// title and copyright pages stay with the Manuscript Structure page — so page
// art goes on chapter openers, body pages and blanks only.

/** Every field, with its range or choices, as the backend checks it. */
export const THEME_LIMITS = {
  page: {
    top: [0.4, 1.5], bottom: [0.4, 1.5], inside: [0.4, 1.5], outside: [0.3, 1.5],
  },
  body: { size: [8, 14], lead: [1.1, 2.2], indent: [0.5, 4] },
  ch: { lSize: [7, 72], lTrack: [-0.05, 0.5], tSize: [10, 48], sink: [0, 50], drop: [2, 5] },
  h: { size: [8, 20] },
  q: { indent: [0, 4], size: [80, 100] },
  run: { size: [6, 12], track: [0, 0.4] },
  art: { inset: [0.1, 1.5], scale: [10, 300], opacity: [0, 100], blur: [0, 8] },
};

/**
 * The six sample artworks. The browser draws public/theme-art/<id>.svg; the PDF
 * uses a raster of the same file (backend/static/theme-art/). `w` and `h` are the
 * size the editor treats each as, in CSS pixels — what Actual size and Tile
 * scale from — and `suggest` is how each is placed when picked, as in the
 * prototype (less the title and copyright pages).
 */
export const ART_SAMPLES = {
  parchment: {
    name: 'Parchment', w: 800, h: 1200,
    suggest: { fit: 'cover', scale: 100, anchor: 'cc', extent: 'bleed', opacity: 45, blend: 'multiply', filter: 'none', blur: 0, fade: 'none', mirror: true, pages: { opener: true, body: true, blank: true } },
  },
  watercolor: {
    name: 'Watercolor', w: 600, h: 900,
    suggest: { fit: 'cover', anchor: 'tc', extent: 'bleed', opacity: 50, blend: 'multiply', filter: 'none', blur: 0, fade: 'bottom', mirror: true, pages: { opener: true, body: false, blank: false } },
  },
  botanical: {
    name: 'Botanical', w: 500, h: 500,
    suggest: { fit: 'actual', scale: 55, anchor: 'tr', extent: 'bleed', opacity: 60, blend: 'multiply', filter: 'none', blur: 0, fade: 'none', mirror: true, pages: { opener: true, body: false, blank: false } },
  },
  mountains: {
    name: 'Mountains', w: 900, h: 420,
    suggest: { fit: 'contain', anchor: 'bc', extent: 'bleed', opacity: 40, blend: 'multiply', filter: 'none', blur: 0, fade: 'top', mirror: false, pages: { opener: true, body: false, blank: false } },
  },
  damask: {
    name: 'Damask', w: 120, h: 120,
    suggest: { fit: 'tile', scale: 100, anchor: 'cc', extent: 'bleed', opacity: 8, blend: 'multiply', filter: 'none', blur: 0, fade: 'vignette', mirror: false, pages: { opener: true, body: true, blank: true } },
  },
  frame: {
    name: 'Frame', w: 600, h: 900,
    suggest: { fit: 'stretch', anchor: 'cc', extent: 'inset', inset: 0.3, opacity: 80, blend: 'multiply', filter: 'none', blur: 0, fade: 'none', mirror: false, pages: { opener: true, body: false, blank: false } },
  },
};

/** The typefaces the Theme Editor offers, grouped as its font menu shows them. */
export const THEME_FONT_GROUPS = [
  { id: 'serif', label: 'Serif · text', fonts: ['EB Garamond', 'Cormorant Garamond', 'Libre Baskerville', 'Libre Caslon Text', 'Crimson Pro', 'Lora', 'Merriweather', 'Source Serif 4', 'Literata', 'Spectral', 'Alegreya'] },
  { id: 'display', label: 'Display', fonts: ['Playfair Display', 'Cinzel', 'Bodoni Moda', 'Oswald', 'Baloo 2'] },
  { id: 'sans', label: 'Sans serif', fonts: ['Inter', 'Montserrat', 'Josefin Sans', 'Work Sans'] },
  { id: 'mono', label: 'Typewriter', fonts: ['Courier Prime'] },
];

export const CASES = [['none', 'Aa'], ['smallcaps', 'Small caps'], ['upper', 'UPPER']];

export const LABEL_STYLES = [
  ['word', 'Chapter One'], ['num', 'Chapter 1'], ['numeral', '1'], ['padded', '01'],
  ['roman', 'I'], ['wordonly', 'One'], ['none', 'No label'],
];

export const ORNAMENTS = [
  ['none', 'None'], ['rule', 'Rule'], ['fleuron', 'Fleuron'], ['flourish', 'Flourish'],
  ['dots', 'Dots'], ['diamond', 'Diamond'], ['stars', 'Star'],
];

export const BREAK_STYLES = [
  ['asterism', 'Asterism'], ['dinkus', 'Dinkus'], ['fleuron', 'Fleuron'], ['dots', 'Dots'],
  ['stars', 'Stars'], ['diamond', 'Diamond'], ['hash', 'Hash'], ['rule', 'Rule'], ['space', 'Blank line'],
];

/**
 * Literary Classic, which is also what any missing value falls back to — the
 * same defaults the backend fills in, generated label and running heads off.
 */
export const BASE_THEME = {
  page: { top: 0.75, bottom: 0.8, inside: 0.875, outside: 0.625 },
  body: { font: 'EB Garamond', size: 11.5, lead: 1.42, align: 'justify', style: 'indent', indent: 1.5, hyph: true },
  ch: {
    recto: true, label: 'none', lFont: 'EB Garamond', lSize: 10.5, lCase: 'smallcaps', lTrack: 0.16,
    tFont: 'EB Garamond', tSize: 24, tWeight: 400, tItalic: true, tCase: 'none', align: 'center',
    sink: 28, orn: 'rule', first: 'smallcaps', drop: 3,
  },
  h: { font: 'EB Garamond', size: 11.5, weight: 400, italic: false, case: 'smallcaps', align: 'center', color: 'text' },
  brk: { style: 'asterism', after: 'smallcaps' },
  q: { italic: true, indent: 2, size: 95, rule: false },
  list: { marker: 'bullet' },
  run: { mode: 'none', role: 'body', case: 'smallcaps', size: 9, track: 0.12 },
  folio: { pos: 'bottom-center', style: 'plain', opener: true },
  // design-tokens-allow — a manuscript's colours are the author's data
  col: { text: '#1d1a17', accent: '#1d1a17', paper: '#fffdf8' },
  // Page art: an image behind the text. `src` is `sample:<id>` or, for the
  // author's own upload, `upload:<key>`.
  art: {
    on: false, src: '', name: '', w: 0, h: 0,
    pages: { opener: true, body: false, blank: false },
    extent: 'bleed', inset: 0.3, fit: 'cover', scale: 100, anchor: 'cc', mirror: true,
    opacity: 35, blend: 'multiply', filter: 'none', blur: 0, fade: 'none',
  },
};

// What a preset that names no label or running head uses once the author turns
// them on — the prototype's own defaults.
const PROTOTYPE_LABEL = 'word';
const PROTOTYPE_RUN_MODE = 'book';

export const THEME_SECTIONS = Object.keys(BASE_THEME);

/** A theme's values over the defaults, one level deep — the prototype's `merge`. */
export function mergeTheme(base, over) {
  const out = {};
  THEME_SECTIONS.forEach((section) => {
    out[section] = { ...base[section], ...((over && over[section]) || {}) };
  });
  return out;
}

const preset = (id, name, blurb, over) => ({ id, name, blurb, ...mergeTheme(BASE_THEME, over) });

/**
 * The generated chapter label and the running heads start switched off on every
 * preset (decided 2026-11-03): an author who typed "Chapter 1" into the
 * manuscript would otherwise get it twice. Each preset keeps its own label and
 * running-head style for when the author turns them on.
 */
const withGeneratedOff = (p) => ({
  ...p,
  labelStyle: p.ch.label === 'none' ? PROTOTYPE_LABEL : p.ch.label,
  runMode: p.run.mode === 'none' ? PROTOTYPE_RUN_MODE : p.run.mode,
  ch: { ...p.ch, label: 'none' },
  run: { ...p.run, mode: 'none' },
});

export const PRESETS = [
  preset('literary', 'Literary Classic', 'Garamond, small-caps openings, centered italic titles', {}),
  preset('modern', 'Modern Clean', 'Source Serif text with bold sans-serif chapter numbers', {
    body: { font: 'Source Serif 4', size: 10.5, lead: 1.5, align: 'left', indent: 1.25, hyph: false },
    ch: { label: 'numeral', lFont: 'Inter', lSize: 40, lCase: 'none', lTrack: -0.02, tFont: 'Inter', tSize: 19, tWeight: 700, tItalic: false, align: 'left', sink: 16, orn: 'rule', first: 'none' },
    h: { font: 'Inter', size: 8.5, weight: 700, case: 'upper', align: 'left', color: 'accent' },
    brk: { style: 'dots', after: 'none' },
    q: { italic: false, rule: true, indent: 1 },
    run: { mode: 'chapter', role: 'label', case: 'upper', size: 7, track: 0.22 },
    folio: { pos: 'top-outside' },
    // design-tokens-allow
    col: { text: '#1f2328', accent: '#5b39c6', paper: '#ffffff' },
  }),
  preset('romance', 'Romance', 'Cormorant text, Playfair italic titles, rose accents', {
    body: { font: 'Cormorant Garamond', size: 12.5, lead: 1.36 },
    ch: { label: 'word', lFont: 'Cormorant Garamond', lSize: 9, lCase: 'upper', lTrack: 0.32, tFont: 'Playfair Display', tSize: 25, tWeight: 400, tItalic: true, orn: 'fleuron', first: 'dropcap', drop: 3, sink: 24 },
    h: { font: 'Playfair Display', size: 11.5, italic: true, case: 'none', color: 'accent' },
    brk: { style: 'fleuron', after: 'none' },
    run: { role: 'label', case: 'upper', size: 7.5, track: 0.28 },
    // design-tokens-allow
    col: { text: '#2a1f22', accent: '#a3445d', paper: '#fffaf7' },
    art: { on: true, src: 'sample:botanical', name: 'Botanical', ...ART_SAMPLES.botanical.suggest },
  }),
  preset('thriller', 'Thriller', 'Spectral text, oversized Oswald chapter numbers', {
    page: { top: 0.7, bottom: 0.75, inside: 0.8, outside: 0.55 },
    body: { font: 'Spectral', size: 10.5, lead: 1.45 },
    ch: { label: 'padded', lFont: 'Oswald', lSize: 56, lCase: 'none', lTrack: 0, tFont: 'Oswald', tSize: 15, tWeight: 600, tItalic: false, tCase: 'upper', align: 'left', sink: 10, orn: 'none', first: 'none' },
    h: { font: 'Oswald', size: 10, weight: 500, case: 'upper', align: 'left', color: 'accent' },
    brk: { style: 'rule', after: 'none' },
    q: { italic: false, rule: true, indent: 1 },
    run: { mode: 'chapter', role: 'heading', case: 'upper', size: 7.5, track: 0.2 },
    folio: { pos: 'bottom-outside' },
    // design-tokens-allow
    col: { text: '#141414', accent: '#c4262e', paper: '#ffffff' },
  }),
  preset('fantasy', 'Epic Fantasy', 'Alegreya text, Cinzel capitals, illuminated drop caps', {
    body: { font: 'Alegreya', size: 11, lead: 1.45 },
    ch: { label: 'wordonly', lFont: 'Cinzel', lSize: 11, lCase: 'upper', lTrack: 0.3, tFont: 'Cinzel', tSize: 21, tWeight: 600, tItalic: false, orn: 'flourish', first: 'both', drop: 4, sink: 26 },
    h: { font: 'Cinzel', size: 10.5, weight: 600, case: 'none', color: 'accent' },
    brk: { style: 'dinkus', after: 'smallcaps' },
    run: { role: 'label', case: 'upper', size: 7, track: 0.26 },
    // design-tokens-allow
    col: { text: '#221c14', accent: '#7a5a1e', paper: '#fbf6ea' },
    art: { on: true, src: 'sample:parchment', name: 'Parchment', ...ART_SAMPLES.parchment.suggest, mirror: false },
  }),
  preset('manuscript', 'Manuscript Submission', 'Agent-ready: Courier 12 pt, double-spaced, 1" margins', {
    page: { top: 1, bottom: 1, inside: 1, outside: 1 },
    body: { font: 'Courier Prime', size: 12, lead: 2, align: 'left', indent: 3, hyph: false },
    ch: { recto: false, label: 'word', lFont: 'Courier Prime', lSize: 12, lCase: 'upper', lTrack: 0, tFont: 'Courier Prime', tSize: 12, tWeight: 400, tItalic: false, sink: 30, orn: 'none', first: 'none' },
    h: { font: 'Courier Prime', size: 12, case: 'none', align: 'left' },
    brk: { style: 'hash', after: 'none' },
    q: { italic: false, indent: 3, size: 100 },
    run: { mode: 'manuscript', role: 'body', case: 'none', size: 12, track: 0 },
    folio: { pos: 'none' },
    // design-tokens-allow
    col: { text: '#000000', accent: '#000000', paper: '#ffffff' },
  }),
  preset('middle', 'Middle Grade', 'Friendly Literata text, big rounded chapter titles', {
    body: { font: 'Literata', size: 12, lead: 1.55, align: 'left', hyph: false },
    ch: { label: 'num', lFont: 'Baloo 2', lSize: 13, lCase: 'none', lTrack: 0.02, tFont: 'Baloo 2', tSize: 28, tWeight: 700, tItalic: false, orn: 'stars', first: 'dropcap', drop: 2, sink: 18 },
    h: { font: 'Baloo 2', size: 13, weight: 700, case: 'none', color: 'accent' },
    brk: { style: 'stars', after: 'none' },
    list: { marker: 'arrow' },
    run: { role: 'heading', case: 'none', size: 9, track: 0.02 },
    // design-tokens-allow
    col: { text: '#2a2633', accent: '#e0672f', paper: '#ffffff' },
  }),
].map(withGeneratedOff);

export const presetById = (id) => PRESETS.find((p) => p.id === id) || null;
